import ReactMarkdown from 'react-markdown'
import { gfmStrikethroughFromMarkdown } from 'mdast-util-gfm-strikethrough'
import { gfmTableFromMarkdown } from 'mdast-util-gfm-table'
import { gfmStrikethrough } from 'micromark-extension-gfm-strikethrough'
import { gfmTable } from 'micromark-extension-gfm-table'

/**
 * The two parts of GFM the lessons use: tables (lesson 15's past-tense
 * paradigm is 14×5) and strikethrough (~~Ja z Ukrainy~~, the wrong form).
 *
 * Not remark-gfm, on purpose. Its bare-URL/e-mail linking builds a
 * lookbehind RegExp at render time, which Safari before 16.4 cannot compile
 * — so on an older iPhone the first lesson section with prose threw and the
 * whole page went white. The lessons have no bare links to make clickable.
 */
function remarkLessonGfm(this: { data(): object }) {
  const data = this.data() as { micromarkExtensions?: unknown[]; fromMarkdownExtensions?: unknown[] }
  ;(data.micromarkExtensions ??= []).push(gfmTable(), gfmStrikethrough())
  ;(data.fromMarkdownExtensions ??= []).push(gfmTableFromMarkdown(), gfmStrikethroughFromMarkdown())
}

type MdNode = { type: string; value?: string; children?: MdNode[]; data?: { hProperties?: Record<string, unknown> } }

/**
 * A bold piece glued to the letters before it — „lekarz**em**”, „Polak**iem**”
 * — is a grammar ending, and the classroom paints endings gold (Lesson.png).
 * Bold that starts a word stays ordinary emphasis.
 */
function remarkEndings() {
  const walk = (node: MdNode) => {
    const kids = node.children ?? []
    kids.forEach((child, i) => {
      const prev = kids[i - 1]
      if (child.type === 'strong' && prev?.type === 'text' && /[\p{L}]$/u.test(prev.value ?? '')) {
        child.data = { ...child.data, hProperties: { ...child.data?.hProperties, 'data-ending': '' } }
      }
      walk(child)
    })
  }
  return (tree: MdNode) => walk(tree)
}

/**
 * Lesson prose. The sections come from the Markdown files verbatim, tables
 * included, so a real renderer is the honest choice over a hand-rolled one.
 *
 * No rehype-raw, deliberately: nothing from the content is ever interpreted
 * as HTML. Diana writes Markdown; if a lesson ever contains a tag, it shows
 * as text, which is the safe failure.
 *
 * `classroom` is the style C look (lesson player, Studio): ink on paper,
 * Polish examples in the display face, wrong forms („*\*Ja lekarz*”) struck
 * through with ✗, endings in gold.
 */
export function Markdown({ children, variant = 'site' }: { children: string; variant?: 'site' | 'classroom' }) {
  if (variant === 'classroom') return <ClassroomMarkdown>{children}</ClassroomMarkdown>
  return (
    <ReactMarkdown
      remarkPlugins={[remarkLessonGfm]}
      components={{
        h3: ({ children }) => <h3 className="h-condensed mt-10 text-lg text-ink first:mt-0">{children}</h3>,
        h4: ({ children }) => <h4 className="mt-6 font-medium">{children}</h4>,
        p: ({ children }) => <p className="my-4 leading-[1.8] font-light text-ink-soft">{children}</p>,
        strong: ({ children }) => <strong className="font-medium text-ink">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        ul: ({ children }) => <ul className="my-4 space-y-1.5 pl-5 font-light text-ink-soft [&>li]:list-disc">{children}</ul>,
        ol: ({ children }) => <ol className="my-4 space-y-1.5 pl-5 font-light text-ink-soft [&>li]:list-decimal">{children}</ol>,
        li: ({ children }) => <li className="leading-relaxed">{children}</li>,
        blockquote: ({ children }) => (
          <blockquote className="my-6 border-l-2 border-gold pl-5 font-light text-ink-soft">{children}</blockquote>
        ),
        hr: () => <hr className="my-8 border-sky-200" />,
        code: ({ children }) => <code className="rounded bg-sky-50 px-1 py-0.5 font-mono text-[0.9em]">{children}</code>,
        /* Wide paradigms scroll inside their own box; the page never scrolls sideways. */
        table: ({ children }) => (
          <div className="my-6 overflow-x-auto rounded-sm border border-sky-200">
            <table className="w-full text-sm">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-sky-50 text-left">{children}</thead>,
        th: ({ children }) => <th className="border-b border-sky-200 px-3 py-2 font-medium">{children}</th>,
        td: ({ children }) => <td className="border-b border-sky-100 px-3 py-2 align-top font-light">{children}</td>,
        a: ({ children, href }) => (
          <a href={href} className="text-gold-600 underline" target="_blank" rel="noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  )
}

function textOf(node: unknown): string {
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(textOf).join('')
  return ''
}

function ClassroomMarkdown({ children }: { children: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkLessonGfm, remarkEndings]}
      components={{
        h3: ({ children }) => <h3 className="cl-subheading mt-8 mb-2 first:mt-0">{children}</h3>,
        h4: ({ children }) => <h4 className="mt-6 mb-1 font-bold">{children}</h4>,
        p: ({ children }) => <p className="my-3 leading-[1.65]">{children}</p>,
        strong: ({ children, node }) =>
          node?.properties && 'dataEnding' in node.properties ? (
            <strong className="font-semibold text-class-gold-700">{children}</strong>
          ) : (
            <strong className="font-semibold">{children}</strong>
          ),
        em: ({ children }) => {
          // „*\*Jestem lekarz*” — the asterisk marks a wrong form.
          const text = textOf(children)
          if (text.startsWith('*')) {
            return (
              <span className="whitespace-nowrap">
                <span aria-hidden className="mr-0.5 font-semibold text-class-wrong">
                  ✗
                </span>
                <s className="text-class-ink-soft">{text.slice(1)}</s>
              </span>
            )
          }
          // Polish examples read in the display face; a UA/RU note in italics stays in the text face.
          return /[\u0400-\u04FF]/.test(text) ? (
            <em className="italic">{children}</em>
          ) : (
            <em lang="pl" className="font-class-display italic">
              {children}
            </em>
          )
        },
        ul: ({ children }) => <ul className="my-3 list-disc space-y-1.5 pl-5 marker:text-class-ink-faint">{children}</ul>,
        ol: ({ children }) => <ol className="my-3 list-decimal space-y-1.5 pl-5 marker:text-class-ink-faint">{children}</ol>,
        li: ({ children }) => <li className="leading-[1.6]">{children}</li>,
        blockquote: ({ children }) => (
          <blockquote lang="pl" className="my-5 border-l-2 border-class-gold-line pl-5 font-class-display text-[16px]">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-6 border-class-line" />,
        code: ({ children }) => <code className="rounded bg-class-field-soft px-1 py-0.5 text-[0.9em]">{children}</code>,
        /* Wide paradigms scroll inside their own box; the page never scrolls sideways. */
        table: ({ children }) => (
          <div className="my-5 overflow-x-auto">
            <table className="w-full border-collapse text-left">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead>{children}</thead>,
        th: ({ children }) => (
          <th className="px-4 pb-2.5 text-class-label font-semibold whitespace-nowrap text-class-ink-faint first:pl-4">{children}</th>
        ),
        td: ({ children }) => (
          <td lang="pl" className="border-t border-class-line px-4 py-3 align-top">
            {children}
          </td>
        ),
        a: ({ children, href }) => (
          <a href={href} className="font-semibold underline" target="_blank" rel="noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  )
}
